/* CEG predictive search — slide-down modal with live suggestions
 * Public surface: window.CEGSearch.open(), .close(), .toggle()
 * Auto-binds:
 *   - [data-ceg-search-open]   → opens modal
 *   - [data-ceg-search-close]  → closes modal
 *   - [data-ceg-search-input]  → debounced predictive search
 * Endpoint: /search/suggest.json
 */
(function () {
  'use strict';

  const DEBOUNCE_MS = 180;
  const SUGGEST_LIMIT = 6;
  const SUGGEST_URL =
    '/search/suggest.json?resources[type]=product,page,article&resources[limit]=' +
    SUGGEST_LIMIT +
    '&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor,body';

  let modal = null;
  let panel = null;
  let backdrop = null;
  let input = null;
  let resultsEl = null;
  let popularEl = null;
  let emptyEl = null;
  let statusEl = null;
  let noResultsTpl = '';
  let lastFocus = null;
  let debounceTimer = null;
  let activeAbort = null;
  let reducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ------------------------------------------------------------------
  // Init
  // ------------------------------------------------------------------
  function init() {
    modal = document.querySelector('[data-ceg-search-modal]');
    if (!modal) return;

    panel = modal.querySelector('[data-ceg-search-panel]');
    backdrop = modal.querySelector('[data-ceg-search-backdrop]');
    input = modal.querySelector('[data-ceg-search-input]');
    resultsEl = modal.querySelector('[data-ceg-search-results]');
    popularEl = modal.querySelector('[data-ceg-search-popular]');
    emptyEl = modal.querySelector('[data-ceg-search-empty]');
    statusEl = modal.querySelector('[data-ceg-search-status]');
    noResultsTpl =
      (emptyEl && emptyEl.getAttribute('data-no-results-template')) ||
      "No matches for “{query}”. Try Amethyst, Quartz, or Custom.";

    // Event delegation — document-level open binding
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onDocKeydown);

    if (input) {
      input.addEventListener('input', onInput);
      input.addEventListener('keydown', onInputKeydown);
    }

    if (backdrop) backdrop.addEventListener('click', close);
  }

  // ------------------------------------------------------------------
  // Open / close
  // ------------------------------------------------------------------
  function open() {
    if (!modal) return;
    lastFocus = document.activeElement;
    modal.hidden = false;
    // Force reflow so the slide-down transition runs
    /* eslint-disable-next-line no-unused-expressions */
    modal.offsetHeight;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('ceg-search-open');

    // Initial state: show popular categories
    showPopular();

    if (input) {
      setTimeout(
        function () {
          input.focus();
          input.select && input.select();
        },
        reducedMotion ? 0 : 60
      );
    }
  }

  function close() {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ceg-search-open');
    // Hide after transition
    setTimeout(
      function () {
        if (!modal.classList.contains('is-open')) modal.hidden = true;
      },
      reducedMotion ? 0 : 320
    );

    if (activeAbort) {
      activeAbort.abort();
      activeAbort = null;
    }
    clearTimeout(debounceTimer);

    if (lastFocus && typeof lastFocus.focus === 'function') {
      try {
        lastFocus.focus();
      } catch (e) {
        /* noop */
      }
    }
  }

  function toggle() {
    if (!modal) return;
    if (modal.classList.contains('is-open')) close();
    else open();
  }

  // ------------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------------
  function onDocClick(e) {
    const opener = e.target.closest && e.target.closest('[data-ceg-search-open]');
    if (opener) {
      e.preventDefault();
      open();
      return;
    }
    const closer = e.target.closest && e.target.closest('[data-ceg-search-close]');
    if (closer) {
      e.preventDefault();
      close();
    }
  }

  function onDocKeydown(e) {
    if (!modal || !modal.classList.contains('is-open')) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Tab') trapFocus(e);
  }

  function onInputKeydown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      const q = (input.value || '').trim();
      if (q) {
        window.location.href = '/search?q=' + encodeURIComponent(q);
      }
    } else if (e.key === 'ArrowDown') {
      const first = resultsEl && resultsEl.querySelector('a, button');
      if (first) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function onInput() {
    const q = (input.value || '').trim();
    clearTimeout(debounceTimer);
    if (!q) {
      showPopular();
      return;
    }
    debounceTimer = setTimeout(function () {
      runSuggest(q);
    }, DEBOUNCE_MS);
  }

  // ------------------------------------------------------------------
  // Suggest API
  // ------------------------------------------------------------------
  function runSuggest(query) {
    if (activeAbort) activeAbort.abort();
    activeAbort =
      typeof AbortController !== 'undefined' ? new AbortController() : null;

    setStatus('Searching…');
    showEmpty(false);
    setBusy(true);

    fetch(SUGGEST_URL + '&q=' + encodeURIComponent(query), {
      headers: { Accept: 'application/json' },
      signal: activeAbort ? activeAbort.signal : undefined,
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Suggest failed: ' + r.status);
        return r.json();
      })
      .then(function (data) {
        renderResults(data, query);
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        // Network or parse error → show empty
        renderResults({ resources: { results: {} } }, query);
        console.warn('[CEGSearch] suggest error', err);
      })
      .finally(function () {
        setBusy(false);
      });
  }

  // ------------------------------------------------------------------
  // Rendering
  // ------------------------------------------------------------------
  function showPopular() {
    if (resultsEl) {
      resultsEl.hidden = true;
      resultsEl.innerHTML = '';
    }
    if (emptyEl) emptyEl.hidden = true;
    if (popularEl) popularEl.hidden = false;
    setStatus('');
  }

  function showEmpty(on) {
    if (emptyEl) emptyEl.hidden = !on;
    if (on && popularEl) popularEl.hidden = true;
    if (on && resultsEl) resultsEl.hidden = true;
  }

  function renderResults(data, query) {
    const results = (data && data.resources && data.resources.results) || {};
    const products = results.products || [];
    const pages = results.pages || [];
    const articles = results.articles || [];
    const total = products.length + pages.length + articles.length;

    if (popularEl) popularEl.hidden = true;

    if (total === 0) {
      if (resultsEl) {
        resultsEl.hidden = true;
        resultsEl.innerHTML = '';
      }
      if (emptyEl) {
        emptyEl.hidden = false;
        const msg = noResultsTpl.replace('{query}', escapeHtml(query));
        const p = emptyEl.querySelector('[data-ceg-search-empty-msg]');
        if (p) p.innerHTML = msg;
      }
      setStatus('No results for “' + query + '”.');
      return;
    }

    if (emptyEl) emptyEl.hidden = true;
    if (!resultsEl) return;

    const items = [];
    products.slice(0, SUGGEST_LIMIT).forEach(function (p) {
      items.push(renderProduct(p));
    });
    pages.slice(0, SUGGEST_LIMIT).forEach(function (p) {
      items.push(renderPage(p));
    });
    articles.slice(0, SUGGEST_LIMIT).forEach(function (a) {
      items.push(renderArticle(a));
    });

    resultsEl.innerHTML =
      '<ul class="ceg-search__list" role="list">' + items.join('') + '</ul>' +
      '<a class="ceg-search__view-all" href="/search?q=' +
      encodeURIComponent(query) +
      '">View all results for “' +
      escapeHtml(query) +
      '” →</a>';
    resultsEl.hidden = false;
    setStatus(total + ' result' + (total === 1 ? '' : 's') + '.');
  }

  function renderProduct(p) {
    const img = p.featured_image || p.image || '';
    const price = p.price ? formatMoney(p.price) : '';
    const compare = p.compare_at_price && p.compare_at_price > p.price ? formatMoney(p.compare_at_price) : '';
    return (
      '<li class="ceg-search__item">' +
        '<a class="ceg-search__row" href="' + escapeAttr(p.url) + '">' +
          '<span class="ceg-search__thumb">' +
            (img
              ? '<img src="' + escapeAttr(img) + '" alt="" loading="lazy" width="56" height="56">'
              : '<span class="ceg-search__thumb-placeholder" aria-hidden="true"></span>') +
          '</span>' +
          '<span class="ceg-search__row-body">' +
            '<span class="ceg-search__row-title">' + escapeHtml(p.title) + '</span>' +
            '<span class="ceg-search__row-meta">' +
              '<span class="ceg-search__type">Product</span>' +
              (price
                ? '<span class="ceg-search__price">' +
                    (compare
                      ? '<s>' + compare + '</s> '
                      : '') +
                    price +
                  '</span>'
                : '') +
            '</span>' +
          '</span>' +
        '</a>' +
      '</li>'
    );
  }

  function renderPage(p) {
    return (
      '<li class="ceg-search__item">' +
        '<a class="ceg-search__row ceg-search__row--text" href="' + escapeAttr(p.url) + '">' +
          '<span class="ceg-search__thumb ceg-search__thumb--icon" aria-hidden="true">' +
            '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>' +
          '</span>' +
          '<span class="ceg-search__row-body">' +
            '<span class="ceg-search__row-title">' + escapeHtml(p.title) + '</span>' +
            '<span class="ceg-search__row-meta"><span class="ceg-search__type">Page</span></span>' +
          '</span>' +
        '</a>' +
      '</li>'
    );
  }

  function renderArticle(a) {
    const img = a.image || (a.featured_image && a.featured_image.url) || '';
    return (
      '<li class="ceg-search__item">' +
        '<a class="ceg-search__row" href="' + escapeAttr(a.url) + '">' +
          '<span class="ceg-search__thumb">' +
            (img
              ? '<img src="' + escapeAttr(img) + '" alt="" loading="lazy" width="56" height="56">'
              : '<span class="ceg-search__thumb-placeholder ceg-search__thumb-placeholder--article" aria-hidden="true">' +
                  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="14" y2="18"/></svg>' +
                '</span>') +
          '</span>' +
          '<span class="ceg-search__row-body">' +
            '<span class="ceg-search__row-title">' + escapeHtml(a.title) + '</span>' +
            '<span class="ceg-search__row-meta"><span class="ceg-search__type">Article</span></span>' +
          '</span>' +
        '</a>' +
      '</li>'
    );
  }

  // ------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------
  function setStatus(msg) {
    if (statusEl) statusEl.textContent = msg || '';
  }
  function setBusy(on) {
    if (!panel) return;
    panel.classList.toggle('is-busy', !!on);
  }

  function trapFocus(e) {
    const focusables = getFocusable(modal);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function getFocusable(root) {
    return Array.prototype.slice
      .call(
        root.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      )
      .filter(function (el) {
        return el.offsetParent !== null;
      });
  }

  function formatMoney(cents) {
    const n = (Number(cents) || 0) / 100;
    try {
      const fmt = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: fmt }).format(n);
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  function escapeAttr(s) {
    return escapeHtml(s);
  }

  // ------------------------------------------------------------------
  // Public surface
  // ------------------------------------------------------------------
  window.CEGSearch = { open: open, close: close, toggle: toggle };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
